// --- src/services/supabaseService.ts ---
// Facade pattern: Re-exports all service functions for backward compatibility

import { DashboardStats, DashboardChartData, TopProduct, LowStockItem } from '../types/dashboard';
import { supabase } from '@/lib/supabaseClient';
import { authService } from './authService';
import { inventoryService } from './inventoryService';
import { posService } from './posService';
import { adminService } from './adminService';
import { getRpcName as getUtilRpcName } from '@/utils/rpc';

/**
 * Helper function to get the correct RPC name based on the active schema.
 *
 * DEPRECATED: Use getRpcName from @/utils/rpc instead.
 * This function is kept for backward compatibility only.
 *
 * @param baseName - Base RPC function name (e.g., 'get_dashboard_kpi_stats')
 * @returns RPC function name with appropriate suffix
 */
const getRpcName = (baseName: string): string => {
  // Delegate to centralized utility
  return getUtilRpcName(baseName);
};

/**
 * Facade service that combines all specialized services
 * Maintains backward compatibility with existing code
 *
 * Note: Some functions may require dependency injection when called directly.
 * For example:
 * - `adminService.logAction()` accepts optional `addToQueue` parameter
 * - `posService.cancelCartTransaction()` accepts optional `updateItemQuantity` parameter
 * - `inventoryService.processQueue()` accepts optional `logAction` parameter
 *
 * When calling through this facade, you can pass these dependencies explicitly,
 * or the services will handle missing dependencies gracefully (e.g., skip queuing if not provided).
 */
export const getDashboardStats = async (
  startDate: Date,
  endDate: Date
): Promise<DashboardStats | null> => {
  const rpcName = getRpcName('get_dashboard_kpi_stats');
  const { data, error } = await supabase.rpc(rpcName, {
    p_start_date: startDate.toISOString(),
    p_end_date: endDate.toISOString(),
  });

  if (error) {
    console.error('Error fetching dashboard stats:', error);
    return null;
  }
  // RPC RETURNS TABLE gives array of rows; we return a single stats object
  const row = Array.isArray(data) && data.length > 0 ? data[0] : data;
  return row as DashboardStats;
};

export const getDashboardChartData = async (
  startDate: Date,
  endDate: Date
): Promise<DashboardChartData[]> => {
  const rpcName = getRpcName('get_dashboard_chart_data');
  const { data, error } = await supabase.rpc(rpcName, {
    p_start_date: startDate.toISOString(),
    p_end_date: endDate.toISOString(),
  });

  if (error) {
    console.error('Error fetching chart data:', error);
    return [];
  }
  return data as DashboardChartData[];
};

export const getDashboardTopProducts = async (
  startDate: Date,
  endDate: Date
): Promise<TopProduct[]> => {
  const rpcName = getRpcName('get_dashboard_top_products');
  const { data, error } = await supabase.rpc(rpcName, {
    p_start_date: startDate.toISOString(),
    p_end_date: endDate.toISOString(),
  });

  if (error) {
    console.error('Error fetching top products:', error);
    return [];
  }
  return data as TopProduct[];
};

export const getDashboardLowStock = async (): Promise<LowStockItem[]> => {
  const rpcName = getRpcName('get_dashboard_low_stock');
  const { data, error } = await supabase.rpc(rpcName);

  if (error) {
    console.error('Error fetching low stock items:', error);
    return [];
  }
  return data as LowStockItem[];
};

export const supabaseService = {
  ...authService,
  ...inventoryService,
  ...posService,
  ...adminService,
  getDashboardStats,
  getDashboardChartData,
  getDashboardTopProducts,
  getDashboardLowStock,
};
